import React, { useEffect, useState } from 'react';
import { Button } from 'antd';
import { IAuthorDeal } from '../../../core/modules/book/book.dto';
import DealLinkArrow from '../../../assets/icons/deal-link-arrow.svg';
import { HUBSPOT_PORTAL_IDS, hubspotDealPageUrl } from '../../../core/constants/global-constants';
import { getEnv } from '../../../config/env-config';
import { formatDate } from './formatters';
import './author-relationship.css';

const PAGE_SIZE = 3;

const env = getEnv();

const dealUrl = (deal: IAuthorDeal): string =>
  hubspotDealPageUrl
    .replace('{portalId}', HUBSPOT_PORTAL_IDS[env])
    .replace('{dealId}', String(deal.deal_id));

const DealRow: React.FC<{ deal: IAuthorDeal }> = ({ deal }) => {
  const stageLabel = deal.stage ?? 'Unknown stage';
  const enteredOn = formatDate(deal.stage_entered_at ?? deal.created_at);

  return (
    <div className='ar-deal' data-testid='author-deal'>
      <a
        className='ar-deal-link'
        href={dealUrl(deal)}
        target='_blank'
        rel='noopener noreferrer'
      >
        <span className='ar-deal-name'>{deal.name ?? 'Untitled deal'}</span>
        <img src={DealLinkArrow} alt='Open deal in HubSpot' className='ar-deal-link-icon' />
      </a>
      <div className='ar-deal-status'>
        <span className={`ar-chip ar-chip-${deal.group ?? 'none'}`}>
          {stageLabel} ({deal.pipeline})
        </span>
        {enteredOn && <span className='ar-deal-date'>on {enteredOn}</span>}
      </div>
      {(deal.reason || deal.closed_note) && (
        <div className='ar-deal-notes'>
          {deal.reason && (
            <p className='ar-deal-note'>
              <strong>Reason:</strong> {deal.reason}
            </p>
          )}
          {deal.closed_note && (
            <p className='ar-deal-note'>
              <strong>Closed Note:</strong> {deal.closed_note}
            </p>
          )}
        </div>
      )}
      <p className='ar-deal-owner'>
        {deal.owner ? `Deal owned by ${deal.owner}` : 'No deal owner assigned'}
      </p>
    </div>
  );
};

interface IAuthorDealsListProps {
  authorName: string;
  deals: IAuthorDeal[];
}

const AuthorDealsList: React.FC<IAuthorDealsListProps> = ({ authorName, deals }) => {
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [authorName]);

  const remaining = deals.length - visibleCount;

  return (
    <section className='ar-deals'>
      <h3 className='overlay-section-title'>Latest deals with {authorName}</h3>
      <div className='ar-deals-body'>
        <div>
          {deals.slice(0, visibleCount).map((deal) => (
            <DealRow key={deal.deal_id} deal={deal} />
          ))}
        </div>
        {remaining > 0 && (
          <Button
            className='ar-green-button'
            onClick={() => setVisibleCount((count) => count + PAGE_SIZE)}
          >
            Show next {Math.min(PAGE_SIZE, remaining)} deals
          </Button>
        )}
      </div>
    </section>
  );
};

export default AuthorDealsList;
